/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Wed May 31 2023
 **/

import { useEffect } from "react";
import { atom, useAtom } from "jotai";

import { useModal } from "@/frameworks/designStuff/components";
import { RestAPIError } from "@/frameworks/libraries/restservice";
import { useGroup, useSpace, useUsers } from "@/frameworks/core/API";
import { APIErrorCode, POPOVER_ID } from "@/frameworks/core/commons";

import { usePopover } from "../usePopover";
import { DeleteGroup, LeaveMember, GroupAloneManager, GroupAloneSpaceManager } from "@/frameworks/core/components";
import { ModalID } from "@/frameworks/core/commons";
import { Configure } from "@/frameworks/libraries/utilities";

const IsAdmin = atom<boolean>(false);

export default function useGroupDelete({ groupID }: { groupID: string }) {
	const [isAdmin, setAdmin] = useAtom(IsAdmin);
	const groupAPI = useGroup({
		onSuccess(name, result) {
			if (name === "user:list") {
				const { data } = result as WebServices.GroupUser.List.Response;
				const isGroupAdmin =
					data?.filter((item: WebServices.GroupUser.List.Item) => item.id === Configure.userID)[0]
						?.authority === "GROUP_ADMIN";
				setAdmin(isGroupAdmin);
				if (isGroupAdmin) {
					groupAPI.invite.list.execute({ groupId: groupID });
				}
			}
		}
	});
	const userAPI = useUsers();
	const spaceAPI = useSpace();
	const modal = useModal();
	const popover = usePopover();

	useEffect(() => {
		if (!groupID) return;
		groupAPI.user.list.execute({ groupId: groupID });
	}, [groupID]);

	const handleDeleteCheck = async ({ groupID }: { groupID: string }) => {
		try {
			const deleteCheck = await groupAPI.user.deleteCheck.execute({ groupId: groupID });
			return {
				isError: false,
				data: deleteCheck.data
			};
		} catch (error) {
			const err = error as RestAPIError;
			return {
				isError: true,
				errorCode: err.data.errorCode as RestAPIError["code"],
				message: err?.data.message,
				payload: err?.data.payload
			};
		}
	};

	const handleLeaveGroup = async (
		event: React.MouseEvent<HTMLLIElement>,
		{ name, groupID }: { name: string; groupID: string }
	) => {
		event.stopPropagation();

		const groupInfo = await groupAPI.inquiry.execute({ groupId: groupID });

		popover.hide(POPOVER_ID.GROUP_MORE_MENU);

		if (groupInfo.data?.initialGroup) {
			modal.show({
				id: ModalID.DELETE_GROUP,
				content: <DeleteGroup name={name} groupID={groupID} />,
				dimensions: {
					width: "440px",
					height: "180px"
				}
			});

			return;
		}

		const checkData = await handleDeleteCheck({ groupID });
		if (!checkData.isError) {
			modal.show({
				id: ModalID.LEAVE_MEMBER,
				content: <LeaveMember name={name} groupID={groupID} />,
				dimensions: {
					width: "440px",
					height: "287px"
				}
			});
			return;
		}
		switch (checkData.errorCode) {
			case APIErrorCode.GROUP_USER_DELETE_FAILED_BY_ADMIN_NEED_ASSIGN_OTHER_SPACE_ADMIN: {
				modal.show({
					id: ModalID.GROUP_ALONE_MANAGER,
					content: <GroupAloneManager name={name} groupID={groupID} />,
					dimensions: {
						width: "440px",
						height: "287px"
					}
				});
				break;
			}
			case APIErrorCode.GROUP_USER_DELETE_FAILED_BY_USER_NEED_ASSIGN_OTHER_SPACE_ADMIN: {
				const message = JSON.parse(checkData.payload) as [{ spaceId: string; spaceName: string }];
				const spaceIDs = message.map((item) => item.spaceId);
				modal.show({
					id: ModalID.GROUP_ALONE_SPACE_MANAGER,
					content: (
						<GroupAloneSpaceManager
							groupID={groupID}
							spaces={
								spaceAPI.mySpaces.list.result.data?.groups
									.find((item) => item.id === groupID)
									?.spaces?.filter(
										(item) => spaceIDs.indexOf(item.spaceId) !== -1
									) as SwaggerModels.MySpaceResponseVO[]
							}
						/>
					),
					dimensions: {
						width: "440px",
						height: "287px"
					}
				});
				break;
			}
			default: {
				break;
			}
		}
	};

	return {
		isAdmin,
		isAlone: groupAPI.user.list.result.data?.length === 1,
		userID: userAPI.me.inquiry.result.data?.userId,
		currentGroupSpaces: spaceAPI.mySpaces.list.result.data?.groups.find((item) => item.id === groupID)?.spaces,
		isLoading: groupAPI.user.list.isLoading,
		handlers: { handleDeleteCheck, handleLeaveGroup }
	};
}
